import { withRequestDeadline } from './requestDeadline'
import { reactive } from 'vue'
import { prepareLocalSessionClose } from './localSession'
import { LocalYjsDocument } from './yjsLocalPersistence'
import { hasLocalSaveFailure } from './localSaveState'

export const webOfflineState = reactive({ updateReady: false, updating: false, error: '', persistent: false, persistenceSupported: false, shellReady: false, usage: 0, quota: 0 })
let activateUpdate: ((reload?: boolean) => Promise<void>) | null = null

/** Verify the built shell's entire asset graph, including lazy chunks and WASM. */
export async function checkShellCache(cache: Pick<CacheStorage, 'match'>, base: string, currentAsset?: string): Promise<boolean> {
  const manifest = await cache.match(new URL('offline-shell.json', base).href, { ignoreSearch: true })
  if (!manifest) return false
  const entries = Object.values(await manifest.json()) as Array<{ file?: string; css?: string[]; assets?: string[] }>
  const files = new Set(['index.html', 'offline-shell.json', 'offline-update-guard.js', 'icon-192.png', 'icon-512.png'])
  for (const entry of entries) for (const file of [entry.file, ...(entry.css || []), ...(entry.assets || [])]) if (file) files.add(file)
  if (currentAsset && ![...files].some(file => new URL(file, base).href === currentAsset)) return false
  for (const file of files) if (!await cache.match(new URL(file, base).href, { ignoreSearch: true })) return false
  return true
}
export async function refreshWebOfflineState(): Promise<void> {
  if (!__IS_WEB__) return
  try {
    webOfflineState.persistenceSupported = Boolean(navigator.storage?.persist)
    webOfflineState.persistent = await navigator.storage?.persisted?.() || false
    const estimate = await navigator.storage?.estimate?.()
    webOfflineState.usage = estimate?.usage || 0
    webOfflineState.quota = estimate?.quota || 0
    webOfflineState.shellReady = typeof caches !== 'undefined' && await checkShellCache(caches, new URL(import.meta.env.BASE_URL, location.href).href, import.meta.url)
  } catch (error) { webOfflineState.error = String(error) }
}
export async function requestPersistentStorage(): Promise<void> {
  try {
    if (!navigator.storage?.persist) throw new Error('This browser does not support persistent storage requests')
    webOfflineState.persistent = await navigator.storage.persist()
    if (!webOfflineState.persistent) webOfflineState.error = 'The browser declined persistent storage. Export a backup of important writing.'
    await refreshWebOfflineState()
  } catch (error) { webOfflineState.error = String(error) }
}
export function setWebUpdateActivator(activate: typeof activateUpdate): void { activateUpdate = activate }
let resumeOtherWindow = () => {}
let resumeTimer: ReturnType<typeof setTimeout> | null = null
export async function handleWebUpdateMessage(event: MessageEvent): Promise<void> {
  if (event.data?.type === 'RESUME_AFTER_WEB_UPDATE') { resumeOtherWindow(); return }
  if (event.data?.type !== 'PREPARE_WEB_UPDATE' || !event.ports[0]) return
  if (webOfflineState.updating) { event.ports[0].postMessage({ saved: false }); return }
  webOfflineState.updating = true
  let resume = () => {}
  try {
    resume = await prepareLocalSessionClose()
    await LocalYjsDocument.persistAll()
    if (hasLocalSaveFailure.value) throw new Error('Local save failed')
    resumeOtherWindow = () => { webOfflineState.updating = false; resume(); if (resumeTimer) clearTimeout(resumeTimer); resumeTimer = null }
    resumeTimer = setTimeout(resumeOtherWindow, 45_000)
    event.ports[0].postMessage({ saved: true })
  } catch { webOfflineState.updating = false; resume(); event.ports[0].postMessage({ saved: false }) }
}
async function prepareOtherWindows(): Promise<void> {
  const registration = await navigator.serviceWorker.getRegistration()
  if (!registration?.waiting && !registration?.active) return
  await withRequestDeadline(() => new Promise<void>((resolve, reject) => {
    const channel = new MessageChannel()
    channel.port1.onmessage = event => {
      channel.port1.close()
      if (event.data?.saved) resolve()
      else reject(new Error('Another app tab could not confirm its local saves. Save and close other tabs before updating.'))
    }
    (registration.waiting || registration.active)!.postMessage({ type: 'PREPARE_OTHER_WINDOWS' }, [channel.port2])
  }), { timeoutMs: 15_000 })
}
export async function applyWebUpdate(): Promise<void> {
  if (!activateUpdate || webOfflineState.updating) return
  webOfflineState.updating = true
  webOfflineState.error = ''
  let resume = () => {}
  let activated = false
  try {
    resume = await prepareLocalSessionClose()
    await LocalYjsDocument.persistAll()
    if (hasLocalSaveFailure.value) throw new Error('Local writing could not be saved. Retry local saves before updating.')
    await prepareOtherWindows()
    await activateUpdate(true)
    activated = true
    // registerSW resolves when activation is requested, before the reload.
    // Keep editing frozen through that gap; recover if the reload fails.
    setTimeout(() => {
      resume()
      webOfflineState.updating = false
      webOfflineState.error = 'The update did not reload this window. Retry, or close and reopen the app.'
    }, 45_000)
  } catch (error) {
    webOfflineState.error = String(error)
    const registration = await navigator.serviceWorker.getRegistration().catch(() => undefined)
    ;(registration?.waiting || registration?.active)?.postMessage({ type: 'RESUME_ALL_WINDOWS' })
  }
  finally { if (!activated) { resume(); webOfflineState.updating = false } }
}
export async function startWebOfflineRuntime(): Promise<void> {
  if (!__IS_WEB__) return
  try {
    navigator.serviceWorker?.addEventListener('message', event => { void handleWebUpdateMessage(event) })
    const { registerSW } = await import('virtual:pwa-register')
    activateUpdate = registerSW({ immediate: true, onNeedRefresh: () => { webOfflineState.updateReady = true }, onOfflineReady: () => { void refreshWebOfflineState() }, onRegisterError: error => { webOfflineState.error = `Offline app installation failed: ${error}` } })
    await refreshWebOfflineState()
  } catch (error) { webOfflineState.error = String(error) }
}
